import { useEffect, useState } from 'react'
import { apiClient } from '../services/api'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { useAuthStore } from '../services/authStore'
import { ui } from '../styles/ui'
import { useTranslation } from '../contexts/TranslationContext'
import { formatDateTime } from '../utils/date'
import { formatConfidence, formatEmptyValue } from '../utils/displayValue'

export default function Selectors() {
  const { user } = useAuthStore()
  const { t } = useTranslation()
  const [selectors, setSelectors] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [toast, setToast] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingId, setPendingId] = useState(null)
  const [actionBusy, setActionBusy] = useState(null)
  const [selectedSelector, setSelectedSelector] = useState(null)
  const [selectorLogs, setSelectorLogs] = useState([])
  const [logLoading, setLogLoading] = useState(false)
  const [viewError, setViewError] = useState('')
  const [confirmAction, setConfirmAction] = useState('delete')
  const [reparseJson, setReparseJson] = useState('')
  const [reparseError, setReparseError] = useState('')

  useEffect(() => {
    fetchSelectors()
  }, [])

  const fetchSelectors = async () => {
    try {
      setLoading(true)
      setError('')
      const res = await apiClient.get('/admin/selectors')
      setSelectors(res.data.data || [])
    } catch (err) {
      console.error('Failed to load selectors', err)
      setError(t('Unable to load selector cache.', 'Unable to load selector cache.'))
    } finally {
      setLoading(false)
    }
  }

  const showToast = (type, message) => {
    setToast({ type, message })
    setTimeout(() => setToast(null), 5000)
  }

  const handleRetrain = (id) => {
    promptAction(id, 'retrain')
  }

  const formatJsonString = (raw) => {
    if (!raw) return ''
    try {
      return JSON.stringify(JSON.parse(raw), null, 2)
    } catch {
      return raw
    }
  }

  const safeParseSelectors = (raw) => {
    if (!raw) return null
    if (typeof raw === 'object') return raw
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }

  const getSelectorStatsFromPayload = (selector) => {
    const payload = safeParseSelectors(selector.selectors)
    if (!payload) return { successCount: 0, failCount: 0, confidenceScore: null }

    const candidates = [
      ...(payload.global || []),
      ...Object.values(payload.layoutMap || {}).flat(),
    ].filter((item) => item && typeof item === 'object')

    if (candidates.length === 0) return { successCount: 0, failCount: 0, confidenceScore: null }

    candidates.sort((a, b) => ((b.rank || 0) + (b.confidenceScore || 0)) - ((a.rank || 0) + (a.confidenceScore || 0)))
    const best = candidates[0]
    return {
      successCount: typeof best.successCount === 'number' ? best.successCount : 0,
      failCount: typeof best.failCount === 'number' ? best.failCount : 0,
      confidenceScore: typeof best.confidenceScore === 'number' ? best.confidenceScore : null,
    }
  }

  const promptAction = (id, action) => {
    setPendingId(id)
    setConfirmAction(action)
    setConfirmOpen(true)
  }

  const promptReparse = (selector) => {
    setPendingId(selector.id)
    setConfirmAction('reparse')
    setReparseJson(formatJsonString(selector.selectors))
    setReparseError('')
    setConfirmOpen(true)
  }

  const confirmActionExecute = async () => {
    if (!pendingId) return
    if (confirmAction === 'reparse') {
      try {
        JSON.parse(reparseJson)
        setReparseError('')
      } catch {
        setReparseError(t('Invalid JSON. Fix the selector payload before saving.', 'Invalid JSON. Fix the selector payload before saving.'))
        return
      }
    }

    try {
      setActionBusy(pendingId)
      if (confirmAction === 'clear') {
        await apiClient.post(`/admin/selectors/${pendingId}/clear`)
        showToast('success', t('Selector cache cleared.', 'Selector cache cleared.'))
      } else if (confirmAction === 'reparse') {
        await apiClient.post(`/admin/selectors/${pendingId}/reparse`, { selectorsJson: reparseJson })
        showToast('success', t('Selector cache reparsed.', 'Selector cache reparsed.'))
      } else if (confirmAction === 'retrain') {
        await apiClient.post(`/admin/selectors/${pendingId}/retrain`)
        showToast('success', t('Selector reset for retraining.', 'Selector reset for retraining.'))
      } else {
        await apiClient.delete(`/admin/selectors/${pendingId}`)
        showToast('success', t('Selector removed.', 'Selector removed.'))
      }
      await fetchSelectors()
      if (selectedSelector?.id === pendingId) {
        if (confirmAction === 'clear' || confirmAction === 'reparse') {
          await fetchSelectorDetails(pendingId)
        } else {
          setSelectedSelector(null)
          setSelectorLogs([])
        }
      }
    } catch (err) {
      console.error('Selector action failed', err)
      if (confirmAction === 'clear') {
        showToast('error', t('Failed to clear selector.', 'Failed to clear selector.'))
      } else if (confirmAction === 'reparse') {
        showToast('error', t('Failed to reparse selector cache.', 'Failed to reparse selector cache.'))
      } else {
        showToast('error', t('Failed to remove selector.', 'Failed to remove selector.'))
      }
    } finally {
      setActionBusy(null)
      if (confirmAction !== 'reparse' || reparseError === '') {
        setConfirmOpen(false)
        setPendingId(null)
        setConfirmAction('delete')
        setReparseJson('')
        setReparseError('')
      }
    }
  }

  const fetchSelectorDetails = async (id) => {
    setViewError('')
    setLogLoading(true)
    try {
      const [selectorRes, logsRes] = await Promise.all([
        apiClient.get(`/admin/selectors/${id}`),
        apiClient.get(`/admin/selectors/${id}/logs`),
      ])
      setSelectedSelector(selectorRes.data.data)
      setSelectorLogs(Array.isArray(logsRes?.data?.data) ? logsRes.data.data : [])
    } catch (err) {
      console.error('Failed to load selector details', err)
      setViewError(t('Unable to load selector log details.', 'Unable to load selector log details.'))
      setSelectedSelector(null)
      setSelectorLogs([])
    } finally {
      setLogLoading(false)
    }
  }

  if (!user?.is_admin) {
    return (
      <Card>
        <p className="text-sm text-foreground">{t('You do not have permission to view this page.', 'You do not have permission to view this page.')}</p>
      </Card>
    )
  }

  return (
    <PageShell>
      {/* Header */}
      <PageHeader
        title={t('Selector Cache', 'Selector Cache')}
        subtitle={t('Manage learned CSS selectors for each QR receipt domain.', 'Manage learned CSS selectors for each QR receipt domain.')}
        actions={<Button variant="secondary" onClick={fetchSelectors}>{t('Refresh', 'Refresh')}</Button>}
      />

      {/* Toast */}
      {toast && (
        <div className={`rounded-lg border px-4 py-2 text-sm ${toast.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {toast.message}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className={ui.alertInfo + ' border-red-200 bg-red-50 text-red-700'}>{error}</div>
      )}

      {/* Selectors table */}
      <div className={ui.card}>
        <table className={ui.table}>
          <thead className={ui.tableHead}>
            <tr>
              <th className={ui.tableHeadCell}>{t('Host', 'Host')}</th>
              <th className={ui.tableHeadCell}>{t('Layout', 'Layout')}</th>
              <th className={`${ui.tableHeadCell} text-right`}>{t('Success', 'Success')}</th>
              <th className={`${ui.tableHeadCell} text-right`}>{t('Fail', 'Fail')}</th>
              <th className={`${ui.tableHeadCell} text-right`}>{t('Confidence', 'Confidence')}</th>
              <th className={ui.tableHeadCell}>{t('Actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-divider">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">{t('Loading selectors...', 'Loading selectors...')}</td>
              </tr>
            ) : selectors.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">{t('No selector cache entries found.', 'No selector cache entries found.')}</td>
              </tr>
            ) : (
              selectors.map((s) => (
                <tr key={s.id} className={ui.tableRow}>
                  <td className={ui.tableCell}>
                    <span className="font-medium text-foreground">{s.host}</span>
                  </td>
                  <td className={ui.tableCellText}>{formatEmptyValue(s.layoutKey)}</td>
                  <td className={`${ui.tableCellText} text-right text-green-700 font-semibold`}>{s.successCount ?? getSelectorStatsFromPayload(s).successCount}</td>
                  <td className={`${ui.tableCellText} text-right text-red-600 font-semibold`}>{s.failCount ?? getSelectorStatsFromPayload(s).failCount}</td>
                  <td className={`${ui.tableCellText} text-right`}>
                    {formatConfidence(s.confidenceScore ?? getSelectorStatsFromPayload(s).confidenceScore)}
                  </td>
                  <td className={ui.tableCell}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        className={ui.buttonSmGhost}
                        disabled={actionBusy === s.id}
                        onClick={() => fetchSelectorDetails(s.id)}
                      >
                        {t('View', 'View')}
                      </button>
                      <button
                        className={ui.buttonSmGhost}
                        disabled={actionBusy === s.id}
                        onClick={() => handleRetrain(s.id)}
                      >
                        {actionBusy === s.id ? t('Working...', 'Working...') : t('Retrain', 'Retrain')}
                      </button>
                      <button
                        className={ui.buttonSmGhost}
                        disabled={actionBusy === s.id}
                        onClick={() => promptReparse(s)}
                      >
                        {t('Reparse', 'Reparse')}
                      </button>
                      <button
                        className={ui.buttonSmGhost}
                        disabled={actionBusy === s.id}
                        onClick={() => promptAction(s.id, 'clear')}
                      >
                        {t('Clear', 'Clear')}
                      </button>
                      <button
                        className={ui.buttonSmDanger}
                        disabled={actionBusy === s.id}
                        onClick={() => promptAction(s.id, 'delete')}
                      >
                        {t('Delete', 'Delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedSelector && (
        <div className={`${ui.card} mt-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('Selector details', 'Selector details')}</h2>
              <p className="text-sm text-muted-foreground">{t('View cached selector rules and recent analysis logs for this host.', 'View cached selector rules and recent analysis logs for this host.')}</p>
            </div>
            <Button variant="secondary" onClick={() => setSelectedSelector(null)}>{t('Close', 'Close')}</Button>
          </div>

          {viewError && (
            <div className={ui.alertInfo + ' border-red-200 bg-red-50 text-red-700 mt-4'}>{viewError}</div>
          )}

          <div className="grid gap-4 sm:grid-cols-3 mt-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground">{t('Host', 'Host')}</p>
              <p className="mt-1 text-sm text-foreground">{selectedSelector.host}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">{t('Layout', 'Layout')}</p>
              <p className="mt-1 text-sm text-foreground">{formatEmptyValue(selectedSelector.layoutKey)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">{t('Confidence', 'Confidence')}</p>
              <p className="mt-1 text-sm text-foreground">{formatConfidence(selectedSelector.confidenceScore ?? getSelectorStatsFromPayload(selectedSelector).confidenceScore)}</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3 mt-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground">{t('Success', 'Success')}</p>
              <p className="mt-1 text-sm text-green-700 font-semibold">{selectedSelector.successCount ?? getSelectorStatsFromPayload(selectedSelector).successCount}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">{t('Fail', 'Fail')}</p>
              <p className="mt-1 text-sm text-red-600 font-semibold">{selectedSelector.failCount ?? getSelectorStatsFromPayload(selectedSelector).failCount}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">{t('Loaded', 'Loaded')}</p>
              <p className="mt-1 text-sm text-foreground">{formatDateTime(selectedSelector.createdAt)}</p>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-sm font-medium text-foreground">{t('Cached selectors', 'Cached selectors')}</p>
            <pre className="mt-2 max-h-64 overflow-auto rounded border bg-surface-elevated p-4 text-xs text-foreground">{(() => {
              try {
                return JSON.stringify(JSON.parse(selectedSelector.selectors), null, 2)
              } catch {
                return selectedSelector.selectors || 'No selector data'
              }
            })()}</pre>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">{t('Recent selector analysis logs', 'Recent selector analysis logs')}</p>
              <span className="text-xs text-muted-foreground">{logLoading ? t('Loading...', 'Loading...') : `${selectorLogs.length} host receipts`}</span>
            </div>
            {logLoading ? (
              <p className="mt-3 text-sm text-muted-foreground">{t('Loading logs...', 'Loading logs...')}</p>
            ) : selectorLogs.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">{t('No selector logs found for this host.', 'No selector logs found for this host.')}</p>
            ) : (
              <div className="mt-3 space-y-4">
                {selectorLogs.map((receipt) => (
                  <div key={receipt.id} className="rounded-lg border border-outline bg-surface p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-medium text-foreground">Receipt {receipt.id}</p>
                        <p className="text-xs text-muted-foreground">{receipt.source} · {receipt.status}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">{formatDateTime(receipt.createdAt)}</p>
                    </div>
                    {receipt.logs.length === 0 ? (
                      <p className="mt-3 text-sm text-muted-foreground">{t('No selector-specific log entries.', 'No selector-specific log entries.')}</p>
                    ) : (
                      <div className="mt-3 space-y-3">
                        {receipt.logs.map((log) => (
                          <div key={log.id} className="rounded border border-outline bg-surface-elevated p-3">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                              <span className="text-xs font-semibold text-foreground">{log.step}</span>
                              <span className="text-xs text-muted-foreground">{log.durationMs != null ? `${log.durationMs}ms` : 'duration unknown'}</span>
                            </div>
                            {log.error && <p className="mt-2 text-sm text-red-700">Error: {log.error}</p>}
                            {log.output && (
                              <pre className="mt-2 overflow-auto rounded bg-surface-elevated p-2 text-xs text-foreground">{JSON.stringify(log.output, null, 2)}</pre>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {receipt.llmDebug && (
                      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-amber-900">LLM debug</p>
                          <span className="text-xs text-amber-700">{receipt.llmDebug.model ?? 'LLM'}</span>
                        </div>
                        {receipt.llmDebug.promptLength != null && (
                          <p className="mt-2 text-xs text-muted-foreground">Prompt length: {receipt.llmDebug.promptLength}</p>
                        )}
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <div>
                            <p className="text-xs font-medium text-amber-700">Prompt</p>
                            <pre className="mt-2 max-h-40 overflow-auto rounded bg-surface-elevated p-2 text-[11px] text-foreground">{receipt.llmDebug.prompt}</pre>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-amber-700">Response</p>
                            <pre className="mt-2 max-h-40 overflow-auto rounded bg-surface-elevated p-2 text-[11px] text-foreground">{receipt.llmDebug.response}</pre>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={confirmAction === 'clear'
          ? t('Clear selector cache', 'Clear selector cache')
          : confirmAction === 'reparse'
            ? t('Reparse selector cache', 'Reparse selector cache')
            : confirmAction === 'retrain'
              ? t('Retrain selector cache', 'Retrain selector cache')
              : t('Delete selector', 'Delete selector')}
        message={confirmAction === 'clear'
          ? t('Clear learned selector data for this host and reset its cache state? The domain will be reparsed from scratch on next scan.', 'Clear learned selector data for this host and reset its cache state? The domain will be reparsed from scratch on next scan.')
          : confirmAction === 'reparse'
            ? t('Review and edit the selector JSON payload. Confirm to normalize and save the reparse result.', 'Review and edit the selector JSON payload. Confirm to normalize and save the reparse result.')
            : confirmAction === 'retrain'
              ? t('Retrain this selector cache entry and rerun the latest linked receipt to learn new selectors.', 'Retrain this selector cache entry and rerun the latest linked receipt to learn new selectors.')
              : t('Delete this selector cache entry? The domain will be reparsed from scratch on next scan.', 'Delete this selector cache entry? The domain will be reparsed from scratch on next scan.')}
        confirmText={confirmAction === 'clear' ? t('Clear', 'Clear') : confirmAction === 'reparse' ? t('Reparse', 'Reparse') : confirmAction === 'retrain' ? t('Retrain', 'Retrain') : t('Delete', 'Delete')}
        cancelText={t('Cancel', 'Cancel')}
        onConfirm={confirmActionExecute}
        onCancel={() => { setConfirmOpen(false); setPendingId(null); setConfirmAction('delete'); setReparseJson(''); setReparseError('') }}
        loading={actionBusy !== null}
      >
        {confirmAction === 'reparse' && (
          <>
            <textarea
              value={reparseJson}
              onChange={(event) => {
                setReparseJson(event.target.value)
                if (reparseError) setReparseError('')
              }}
              rows={16}
              spellCheck={false}
              className="mt-4 w-full rounded border border-outline bg-surface-elevated p-3 text-xs text-foreground font-mono shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {reparseError && (
              <div className="mt-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {reparseError}
              </div>
            )}
          </>
        )}
      </ConfirmDialog>
    </PageShell>
  )
}


